import type {
	CustomerPortalRetailPriceRowResultFieldsFragment,
	CustomerPortalRetailPriceSaveFieldsFragment
} from "~/gql/queries/portalRetailPrices.generated";
import type {
	RetailPriceDraft,
	RetailPriceDraftEdit,
	RetailPriceRowError,
	RetailPriceSubmittedRow
} from "~portal/catalog/helpers/retailPriceDraft";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
	type BackgroundJobStatus,
	CustomerPortalRetailPriceAction,
	CustomerPortalRetailPriceRowStatus
} from "~/gql/graphqlBase.generated";
import { setPortalTenant } from "~/store/portalSlice";
import tenantChanged from "~/store/tenantChanged";
import retailPriceSave, { RETAIL_PRICE_SAVE_STALL_MS } from "~portal/catalog/helpers/retailPriceSave";

export interface RetailPriceDraftScope {
	customerId: string;
	tenantId: string;
}

export type RetailPriceSavePhase =
	| "collecting"
	| "finished"
	| "polling"
	| "refused"
	| "stalled"
	| "submitting"
	| "uncertain";

export interface RetailPriceSaveProgress {
	done: number;
	failed: number;
	signature: string;
	status: BackgroundJobStatus;
	total: number;
}

export interface RetailPriceSaveResult {
	errors: RetailPriceRowError[];
	itemId: string;
	status: CustomerPortalRetailPriceRowStatus;
}

export interface RetailPriceSaveState {
	errors: RetailPriceRowError[];
	jobId: string;
	lastProgressAt: number;
	lastReadAt: number;
	phase: RetailPriceSavePhase;
	progress?: RetailPriceSaveProgress;
	results: RetailPriceSaveResult[];
	rows: RetailPriceSubmittedRow[];
}

export interface PortalRetailPriceDraftState {
	drafts: Record<string, RetailPriceDraft>;
	lastVersion: number;
	save?: RetailPriceSaveState;
	scope?: RetailPriceDraftScope;
}

interface ScopedPayload {
	scope: RetailPriceDraftScope;
}

interface SavePayload extends ScopedPayload {
	jobId: string;
}

const initialState: PortalRetailPriceDraftState = {
	drafts: {},
	lastVersion: 0
};

const ACTIVE_PHASES: readonly RetailPriceSavePhase[] = ["collecting", "polling", "stalled", "submitting", "uncertain"];

const ACKNOWLEDGED_STATUSES: readonly CustomerPortalRetailPriceRowStatus[] = [
	CustomerPortalRetailPriceRowStatus.Applied,
	CustomerPortalRetailPriceRowStatus.Unchanged
];

const NOT_ATTEMPTED_STATUSES: readonly CustomerPortalRetailPriceRowStatus[] = [
	CustomerPortalRetailPriceRowStatus.Pending,
	CustomerPortalRetailPriceRowStatus.Skipped
];

export const isRetailPriceSaveActive = (save: RetailPriceSaveState | undefined): boolean =>
	save !== undefined && ACTIVE_PHASES.includes(save.phase);

const isSameScope = (a: RetailPriceDraftScope | undefined, b: RetailPriceDraftScope): boolean =>
	a?.tenantId === b.tenantId && a?.customerId === b.customerId;

const enterScope = (state: PortalRetailPriceDraftState, scope: RetailPriceDraftScope): void => {
	if (isSameScope(state.scope, scope)) return;
	state.drafts = {};
	delete state.save;
	state.scope = { ...scope };
};

const saveInPhase = (
	state: PortalRetailPriceDraftState,
	{ jobId, scope }: SavePayload,
	phases: readonly RetailPriceSavePhase[]
): RetailPriceSaveState | undefined =>
	isSameScope(state.scope, scope) && state.save?.jobId === jobId && phases.includes(state.save.phase)
		? state.save
		: undefined;

const applyProgress = (
	save: RetailPriceSaveState,
	fields: CustomerPortalRetailPriceSaveFieldsFragment,
	readAt: number
): void => {
	const signature = retailPriceSave.progressSignature(fields);
	if (signature !== save.progress?.signature) save.lastProgressAt = readAt;
	save.progress = { done: fields.done, failed: fields.failed, signature, status: fields.status, total: fields.total };
	if (!retailPriceSave.isOpenStatus(fields.status)) save.phase = "collecting";
};

const toOutcomeErrors = (errors: readonly RetailPriceRowError[]): RetailPriceRowError[] =>
	errors.map(({ code, message }) => ({ code, message }));

const keepForReview = (
	draft: RetailPriceDraft,
	row: CustomerPortalRetailPriceRowResultFieldsFragment,
	adoptBaseline: boolean
): void => {
	draft.outcome = { errors: toOutcomeErrors(row.errors), status: row.status };
	if (adoptBaseline) draft.baselineToken = row.customerRetailPrice?.baselineToken ?? null;
};

const reconcileDraft = (
	state: PortalRetailPriceDraftState,
	submitted: RetailPriceSubmittedRow | undefined,
	row: CustomerPortalRetailPriceRowResultFieldsFragment
): void => {
	const draft = state.drafts[row.itemId];
	if (!draft || submitted?.input.itemId !== row.itemId) return;
	if (ACKNOWLEDGED_STATUSES.includes(row.status)) {
		if (draft.version === submitted.version) delete state.drafts[row.itemId];
		else draft.baselineToken = row.customerRetailPrice?.baselineToken ?? null;
		return;
	}
	keepForReview(draft, row, !NOT_ATTEMPTED_STATUSES.includes(row.status));
};

export const portalRetailPriceDraftSlice = createSlice({
	extraReducers: (builder) => {
		builder.addCase(tenantChanged, () => initialState);
		builder.addCase(setPortalTenant, () => initialState);
	},
	initialState,
	name: "portalRetailPriceDraft",
	reducers: {
		clearRetailPriceDrafts: (state, action: PayloadAction<ScopedPayload>) => {
			if (isSameScope(state.scope, action.payload.scope)) state.drafts = {};
		},
		removeRetailPriceDraft: (state, action: PayloadAction<ScopedPayload & { itemId: string }>) => {
			if (isSameScope(state.scope, action.payload.scope)) delete state.drafts[action.payload.itemId];
		},
		resumeRetailPriceSave: (state, action: PayloadAction<SavePayload & { at: number }>) => {
			const save = saveInPhase(state, action.payload, ["stalled"]);
			if (!save) return;
			save.phase = "polling";
			save.lastProgressAt = action.payload.at;
			save.lastReadAt = action.payload.at;
		},
		retryRetailPriceSave: (state, action: PayloadAction<SavePayload>) => {
			const save = saveInPhase(state, action.payload, ["uncertain"]);
			if (save) save.phase = "submitting";
		},
		setRetailPriceDraftScope: (state, action: PayloadAction<RetailPriceDraftScope>) => {
			enterScope(state, action.payload);
		},
		setRetailPriceSaveRefused: (
			state,
			action: PayloadAction<
				SavePayload & { errors: RetailPriceRowError[]; invalidRows: CustomerPortalRetailPriceRowResultFieldsFragment[] }
			>
		) => {
			const save = saveInPhase(state, action.payload, ["submitting"]);
			if (!save) return;
			save.phase = "refused";
			save.errors = toOutcomeErrors(action.payload.errors);
			for (const row of action.payload.invalidRows) {
				const draft = state.drafts[save.rows[row.rowIndex]?.input.itemId ?? ""];
				if (draft) draft.outcome = { errors: toOutcomeErrors(row.errors), status: row.status };
			}
		},
		setRetailPriceSaveResults: (
			state,
			action: PayloadAction<SavePayload & { rows: CustomerPortalRetailPriceRowResultFieldsFragment[] }>
		) => {
			const save = saveInPhase(state, action.payload, ["collecting"]);
			if (!save) return;
			for (const row of action.payload.rows) reconcileDraft(state, save.rows[row.rowIndex], row);
			save.results = action.payload.rows.map((row) => ({
				errors: toOutcomeErrors(row.errors),
				itemId: row.itemId,
				status: row.status
			}));
			save.phase = "finished";
		},
		setRetailPriceSaveStalled: (state, action: PayloadAction<SavePayload>) => {
			const save = saveInPhase(state, action.payload, ["collecting", "polling"]);
			if (save) save.phase = "stalled";
		},
		setRetailPriceSaveStored: (
			state,
			action: PayloadAction<SavePayload & { readAt: number; save: CustomerPortalRetailPriceSaveFieldsFragment }>
		) => {
			const save = saveInPhase(state, action.payload, ["submitting"]);
			if (!save) return;
			save.phase = "polling";
			save.lastProgressAt = action.payload.readAt;
			save.lastReadAt = action.payload.readAt;
			applyProgress(save, action.payload.save, action.payload.readAt);
		},
		setRetailPriceSaveUncertain: (state, action: PayloadAction<SavePayload>) => {
			const save = saveInPhase(state, action.payload, ["submitting"]);
			if (save) save.phase = "uncertain";
		},
		startRetailPriceSave: (
			state,
			action: PayloadAction<SavePayload & { rows: RetailPriceSubmittedRow[]; startedAt: number }>
		) => {
			const { jobId, rows, scope, startedAt } = action.payload;
			enterScope(state, scope);
			if (isRetailPriceSaveActive(state.save) || rows.length === 0) return;
			for (const { input } of rows) {
				const draft = state.drafts[input.itemId];
				if (draft) delete draft.outcome;
			}
			state.save = {
				errors: [],
				jobId,
				lastProgressAt: startedAt,
				lastReadAt: startedAt,
				phase: "submitting",
				results: [],
				rows
			};
		},
		updateRetailPriceSaveProgress: (
			state,
			action: PayloadAction<SavePayload & { readAt: number; save?: CustomerPortalRetailPriceSaveFieldsFragment | null }>
		) => {
			const save = saveInPhase(state, action.payload, ["polling"]);
			if (!save) return;
			const { readAt, save: fields } = action.payload;
			save.lastReadAt = readAt;
			if (fields) applyProgress(save, fields, readAt);
			if (save.phase === "polling" && readAt - save.lastProgressAt >= RETAIL_PRICE_SAVE_STALL_MS)
				save.phase = "stalled";
		},
		upsertRetailPriceDraft: (
			state,
			action: PayloadAction<
				ScopedPayload & { baselineToken: null | string; edit: RetailPriceDraftEdit; itemId: string }
			>
		) => {
			const { baselineToken, edit, itemId, scope } = action.payload;
			enterScope(state, scope);
			state.lastVersion += 1;
			const existing = state.drafts[itemId] ?? {
				action: CustomerPortalRetailPriceAction.Set,
				baselineToken,
				endText: "",
				itemId,
				priceText: "",
				startText: ""
			};
			state.drafts[itemId] = { ...existing, ...edit, itemId, version: state.lastVersion };
		}
	}
});

const EMPTY_DRAFTS: Record<string, RetailPriceDraft> = {};

interface RetailPriceDraftRootState {
	portalRetailPriceDraft: PortalRetailPriceDraftState;
}

export const selectRetailPriceDrafts = (
	state: RetailPriceDraftRootState,
	scope: RetailPriceDraftScope | null
): Record<string, RetailPriceDraft> =>
	scope && isSameScope(state.portalRetailPriceDraft.scope, scope) ? state.portalRetailPriceDraft.drafts : EMPTY_DRAFTS;

export const selectRetailPriceSave = (
	state: RetailPriceDraftRootState,
	scope: RetailPriceDraftScope | null
): RetailPriceSaveState | undefined =>
	scope && isSameScope(state.portalRetailPriceDraft.scope, scope) ? state.portalRetailPriceDraft.save : undefined;

export const {
	clearRetailPriceDrafts,
	removeRetailPriceDraft,
	resumeRetailPriceSave,
	retryRetailPriceSave,
	setRetailPriceDraftScope,
	setRetailPriceSaveRefused,
	setRetailPriceSaveResults,
	setRetailPriceSaveStalled,
	setRetailPriceSaveStored,
	setRetailPriceSaveUncertain,
	startRetailPriceSave,
	updateRetailPriceSaveProgress,
	upsertRetailPriceDraft
} = portalRetailPriceDraftSlice.actions;

export default portalRetailPriceDraftSlice.reducer;
